import React, { FC } from 'react';
import moment from 'moment';

import { Routes } from '@common/constants';
import SectionContainer from '@common/components/SectionContainer';
import { useRouter } from 'shared-routing';
import { useTranslations } from '@lang';
import { useCheckForArrivalDate } from '@common/hooks';
import { DateCategoryPicker } from '@features/JourneyPicker';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { useGetSpaCategories } from '@features/spa/hooks/useGetSpaCategories';

import { SpaListTemplate } from 'voyager';

import { SpaTreatmentsV2Props } from './SpaTreatmentsV2.types';
import { useSpaEntityFilter } from './hooks/useSpaEntityFilter';
import { useSpaTreatmentsList } from './hooks/useSpaTreatmentsList';

export const SpaTreatmentsV2: FC<SpaTreatmentsV2Props> = ({
  property,
  journeyParams,
  changeJourneyParam,
  categoryId = null,
  language,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const { arrivalDate } = router.query;

  useCheckForArrivalDate({
    newArrivalDate: journeyParams.fromDate as string,
    router,
    route: Routes.SPA_TREATMENT_LIST,
  });

  const filter = useSpaEntityFilter({
    categoryId,
    arrivalDate: arrivalDate as string | undefined,
    fromDate: journeyParams?.fromDate as string | undefined,
    changeJourneyParam,
    listingRoute: Routes.SPA_TREATMENT_LIST,
  });

  const { listItems, isFetching } = useSpaTreatmentsList({
    propertyId: property.id,
    language,
    categoryFilterId: filter.categoryFilterId,
    date: filter.appliedDateString,
  });

  const { data: treatmentsCategories = [] } = useGetSpaCategories(
    property.id,
    'treatments',
    language
  );

  const allCategoriesOption = {
    label: t('common.allCategories'),
    value: '',
  };

  const categoryOptions = [
    allCategoriesOption,
    ...treatmentsCategories.map((category) => ({
      label: category.name,
      value: String(category.id),
    })),
  ];

  const currentSelectedCategory = categoryOptions.find(
    ({ value }) => String(value) === String(filter.selectedCategory)
  );

  const handleSelectTreatment = (id: string) => {
    router.push({ pathname: Routes.SPA_TREATMENT_DETAIL, query: { id } });
  };

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <DateCategoryPicker
            fromDate={moment(filter.pickerDate)}
            onDateChange={(d) => filter.setPickerDate(d.format())}
            categories={categoryOptions}
            onCategoryChange={filter.handleCategoryChange}
            selectedCategory={currentSelectedCategory}
            handleUpdate={filter.handleUpdate}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <SpaListTemplate
          items={isFetching ? [] : listItems}
          onSelectItem={handleSelectTreatment}
          isLoading={isFetching}
          categoryOptions={categoryOptions}
          selectedCategory={filter.selectedCategory || ''}
          onCategoryChange={filter.handleDirectCategoryChange}
          titleLabel={t('app.spaTreatments')}
          categoryLabel={t('common.category')}
          fromPriceLabel={t('common.from')}
          soldOutLabel={t('spa.soldOut')}
          noResultsLabel={t('spa.noResults')}
          noImageLabel={t('spa.noImage')}
          formatGuests={(n) => t('common.numberOfGuests', { guestsAmount: n })}
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
